import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import API from "../constants/api-endpoints";

// ─── Dropdown ────────────────────────────────────────────────────────────────

export const useDocClients = (cid: number) =>
  useQuery({ queryKey: ["doc-clients", cid], queryFn: () => client.get(API.DOC_CLIENTS(cid)).then(r => r.data.data) });

// ─── Folders ─────────────────────────────────────────────────────────────────

export const useFolders = (cid: number, params?: any) =>
  useQuery({ queryKey: ["doc-folders", cid, params], queryFn: () => client.get(API.DOC_FOLDERS(cid), { params }).then(r => r.data.data) });

export const useFolderCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.DOC_FOLDERS(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-folders", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useFolderUpdate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => client.put(`${API.DOC_FOLDERS(cid)}/${id}`, data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-folders", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useFolderDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.DOC_FOLDERS(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-folders", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

// ─── Documents ────────────────────────────────────────────────────────────────

export const useDocFiles = (cid: number, params?: any) =>
  useQuery({ queryKey: ["doc-files", cid, params], queryFn: () => client.get(API.DOC_FILES(cid), { params }).then(r => r.data) });

export const useDocFileCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.DOC_FILES(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-files", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useDocFileUpdate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => client.put(`${API.DOC_FILES(cid)}/${id}`, data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-files", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useDocFileDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.DOC_FILES(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-files", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

// ─── Certificate Templates ────────────────────────────────────────────────────

export const useCertTemplates = (cid: number) =>
  useQuery({ queryKey: ["doc-cert-templates", cid], queryFn: () => client.get(API.DOC_CERT_TEMPLATES(cid)).then(r => r.data.data) });

export const useCertTemplateCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.DOC_CERT_TEMPLATES(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-cert-templates", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useCertTemplateUpdate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => client.put(`${API.DOC_CERT_TEMPLATES(cid)}/${id}`, data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-cert-templates", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useCertTemplateDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.DOC_CERT_TEMPLATES(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-cert-templates", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

// ─── Certificates ─────────────────────────────────────────────────────────────

export const useCertificates = (cid: number, params?: any) =>
  useQuery({ queryKey: ["doc-certs", cid, params], queryFn: () => client.get(API.DOC_CERTIFICATES(cid), { params }).then(r => r.data) });

export const useCertificateCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.DOC_CERTIFICATES(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-certs", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useCertificateDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.DOC_CERTIFICATES(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-certs", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

// ─── Client Feedback ─────────────────────────────────────────────────────────

export const useFeedback = (cid: number, params?: any) =>
  useQuery({ queryKey: ["doc-feedback", cid, params], queryFn: () => client.get(API.DOC_FEEDBACK(cid), { params }).then(r => r.data) });

export const useFeedbackCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.DOC_FEEDBACK(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-feedback", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useFeedbackDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.DOC_FEEDBACK(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-feedback", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

// ─── Tenders ─────────────────────────────────────────────────────────────────

export const useTenders = (cid: number, params?: any) =>
  useQuery({ queryKey: ["doc-tenders", cid, params], queryFn: () => client.get(API.DOC_TENDERS(cid), { params }).then(r => r.data) });

export const useTenderCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.DOC_TENDERS(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-tenders", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useTenderUpdate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => client.put(`${API.DOC_TENDERS(cid)}/${id}`, data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-tenders", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useTenderDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.DOC_TENDERS(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-tenders", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

// ─── Contracts ────────────────────────────────────────────────────────────────

export const useContracts = (cid: number, params?: any) =>
  useQuery({ queryKey: ["doc-contracts", cid, params], queryFn: () => client.get(API.DOC_CONTRACTS(cid), { params }).then(r => r.data) });

export const useContractCreate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API.DOC_CONTRACTS(cid), data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-contracts", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useContractUpdate = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => client.put(`${API.DOC_CONTRACTS(cid)}/${id}`, data).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-contracts", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};

export const useContractDelete = (cid: number, cb?: any) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: any) => client.delete(`${API.DOC_CONTRACTS(cid)}/${id}`).then(r => r.data),
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: ["doc-contracts", cid] }); cb?.onSuccess?.(...a); },
    onError: cb?.onError,
  });
};
